import { useEffect, useRef, useState } from "react";
import { Wallet, History, Search, Undo2, Download, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, formatRupiah, formatTanggal, BULAN } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/ConfirmDialog";

export default function Spp() {
  const { user, schoolId, academicYear, setAcademicYear } = useAuth();
  const canEdit = ["super_admin", "bendahara"].includes(user.role);
  const now = new Date();
  const defaultAy = now.getMonth() + 1 >= 7 ? `${now.getFullYear()}/${now.getFullYear() + 1}` : `${now.getFullYear() - 1}/${now.getFullYear()}`;
  const ay = academicYear || defaultAy;
  const ayY1 = Number(ay.split("/")[0]) || now.getFullYear();
  const ayMonths = [7, 8, 9, 10, 11, 12, 1, 2, 3, 4, 5, 6].map((m) => ({ m, y: m >= 7 ? ayY1 : ayY1 + 1 }));
  const nowInAy = ayMonths.some((x) => x.m === now.getMonth() + 1 && x.y === now.getFullYear());
  const [month, setMonth] = useState(String(nowInAy ? now.getMonth() + 1 : 7));
  const [year, setYear] = useState(String(nowInAy ? now.getFullYear() : ayY1));
  const reqSeq = useRef(0);
  const [ayears, setAyears] = useState([]);
  const [data, setData] = useState(null);
  const [needsSchool, setNeedsSchool] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("semua");
  const [classFilter, setClassFilter] = useState("semua");
  const [payTarget, setPayTarget] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", method: "Tunai", note: "" });
  const [saving, setSaving] = useState(false);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [history, setHistory] = useState([]);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [nominalOpen, setNominalOpen] = useState(false);
  const [nominalVal, setNominalVal] = useState("");
  const [savingNominal, setSavingNominal] = useState(false);

  const handleSaveNominal = async (e) => {
    e.preventDefault();
    setSavingNominal(true);
    try {
      await api.put("/schools/current/nominal", { spp_nominal: Number(nominalVal) || 0 });
      toast.success("Nominal SPP berhasil diubah");
      setNominalOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSavingNominal(false);
    }
  };

  const load = () => {
    setNeedsSchool(false);
    const seq = ++reqSeq.current;
    api.get(`/spp/status?month=${month}&year=${year}`)
      .then((r) => { if (seq === reqSeq.current) setData(r.data); })
      .catch((e) => {
        if (seq !== reqSeq.current) return;
        if (e.response?.status === 400) setNeedsSchool(true);
        else {
          setData({ spp_nominal: 0, rows: [] });
          toast.error(apiError(e));
        }
      });
  };

  useEffect(() => {
    if (user.role === "super_admin" && !schoolId) {
      setNeedsSchool(true);
      setData(null);
      return;
    }
    api.get("/academic-years").then((r) => setAyears(r.data.years || [])).catch(() => {});
  }, [schoolId]);

  useEffect(() => {
    if (!ayMonths.some((x) => x.m === Number(month) && x.y === Number(year))) {
      if (ay === defaultAy) {
        setMonth(String(now.getMonth() + 1));
        setYear(String(now.getFullYear()));
      } else {
        setMonth("7");
        setYear(String(ayY1));
      }
    }
  }, [ay]);

  useEffect(() => {
    if (user.role === "super_admin" && !schoolId) {
      setNeedsSchool(true);
      setData(null);
      return;
    }
    load();
  }, [month, year, schoolId]);

  const openPay = (row) => {
    setPayTarget(row.student);
    const sisa = Math.max((data.spp_nominal || 0) - (row.total_paid || 0), 0);
    setPayForm({ amount: String(sisa || data.spp_nominal || ""), method: "Tunai", note: row.status === "sebagian" ? "Cicilan" : "" });
  };

  const handlePay = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/spp/pay", {
        student_id: payTarget.id,
        month: Number(month),
        year: Number(year),
        amount: Number(payForm.amount) || null,
        method: payForm.method,
        note: payForm.note,
      });
      toast.success(`Pembayaran SPP ${payTarget.name} tercatat`);
      setPayTarget(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
      load();
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async (row) => {
    setHistoryTarget(row.student);
    setHistory([]);
    try {
      const r = await api.get(`/spp/history?student_id=${row.student.id}`);
      setHistory(r.data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const handleCancel = async () => {
    try {
      await api.delete(`/spp/payment/${cancelTarget.id}`);
      toast.success("Pembayaran dibatalkan");
      setCancelTarget(null);
      setHistoryTarget(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (needsSchool) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="spp-need-school">
        <p className="font-heading text-lg font-semibold text-slate-800">Pilih sekolah terlebih dahulu</p>
        <p className="mt-1 text-sm text-slate-500">Gunakan pemilih sekolah di pojok kiri atas.</p>
      </div>
    );
  }

  const classes = [...new Set((data?.rows || []).map((r) => r.student.class_name))].sort();
  const rows = (data?.rows || []).filter((r) => {
    const matchSearch =
      r.student.name.toLowerCase().includes(search.toLowerCase()) ||
      r.student.nisn.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === "semua" || r.status === filter;
    const matchClass = classFilter === "semua" || r.student.class_name === classFilter;
    return matchSearch && matchFilter && matchClass;
  });
  const lunasCount = (data?.rows || []).filter((r) => r.status === "lunas").length;
  const sebagianCount = (data?.rows || []).filter((r) => r.status === "sebagian").length;

  return (
    <div className="space-y-6" data-testid="spp-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">SPP Bulanan</h1>
          <p className="mt-1 text-sm text-slate-500">
            Nominal SPP: <span className="font-semibold text-slate-700">{formatRupiah(data?.spp_nominal)}</span> per bulan
            {canEdit && (
              <button onClick={() => { setNominalVal(String(data?.spp_nominal || "")); setNominalOpen(true); }} data-testid="spp-edit-nominal" className="ml-2 inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium text-[#0060AF] hover:bg-[#E5F0FA] transition-colors">
                <Pencil className="h-3 w-3" strokeWidth={1.5} /> Ubah Nominal
              </button>
            )}
          </p>
        </div>
        <Button variant="outline" onClick={async () => {
          try {
            const res = await api.get(`/export/spp?month=${month}&year=${year}`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `rekap_spp_${BULAN[Number(month) - 1]}_${year}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Rekap SPP berhasil diunduh");
          } catch { toast.error("Gagal mengunduh rekap SPP"); }
        }} data-testid="spp-export-button" className="gap-2 rounded-md">
          <Download className="h-4 w-4" strokeWidth={1.5} /> Ekspor Excel
        </Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="mb-5 flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label>Bulan</Label>
            <Select value={`${month}|${year}`} onValueChange={(v) => { const [m, y] = v.split("|"); setMonth(m); setYear(y); }}>
              <SelectTrigger className="w-[170px]" data-testid="spp-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ayMonths.map((x) => (
                  <SelectItem key={`${x.m}-${x.y}`} value={`${x.m}|${x.y}`} data-testid={`spp-month-option-${x.m}`}>{BULAN[x.m - 1]} {x.y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Tahun Ajaran</Label>
            <Select value={ay} onValueChange={(v) => { const y1 = Number(v.split("/")[0]); setMonth("7"); setYear(String(y1)); if (typeof setAcademicYear === "function") setAcademicYear(v); }}>
              <SelectTrigger className="w-[140px]" data-testid="spp-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(ayears.length ? ayears : [ay]).map((y) => (
                  <SelectItem key={y} value={y} data-testid={`spp-year-option-${y.replace("/", "-")}`}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[130px]" data-testid="spp-filter-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="semua" data-testid="spp-filter-semua">Semua</SelectItem>
                <SelectItem value="lunas" data-testid="spp-filter-lunas">Lunas</SelectItem>
                <SelectItem value="sebagian" data-testid="spp-filter-sebagian">Sebagian</SelectItem>
                <SelectItem value="belum" data-testid="spp-filter-belum">Belum</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Kelas</Label>
            <Select value={classFilter} onValueChange={setClassFilter}>
              <SelectTrigger className="w-[120px]" data-testid="spp-class-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="semua" data-testid="spp-class-semua">Semua</SelectItem>
                {classes.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`spp-class-${c}`}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="relative ml-auto w-full max-w-xs space-y-1.5">
            <Label>Cari Siswa</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" strokeWidth={1.5} />
              <Input placeholder="Nama atau NISN..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="spp-search-input" />
            </div>
          </div>
        </div>

        {data && (
          <p className="mb-3 text-sm text-slate-500" data-testid="spp-summary-text">
            <span className="font-semibold text-emerald-600">{lunasCount} lunas</span>
            {" · "}
            <span className="font-semibold text-amber-600">{sebagianCount} sebagian</span>
            {" · "}
            <span className="font-semibold text-rose-600">{data.rows.length - lunasCount - sebagianCount} belum</span>
            {" dari "}{data.rows.length} siswa — {BULAN[Number(month) - 1]} {year}
          </p>
        )}

        <div className="overflow-x-auto">
          <Table data-testid="spp-table">
            <TableHeader>
              <TableRow>
                <TableHead>NISN</TableHead>
                <TableHead>Nama Siswa</TableHead>
                <TableHead>Kelas</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Jumlah</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data === null && (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-slate-500">Memuat data...</TableCell></TableRow>
              )}
              {data !== null && rows.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-slate-500">Tidak ada data yang cocok.</TableCell></TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.student.id} className="transition-colors duration-200 hover:bg-slate-50" data-testid={`spp-row-${r.student.nisn}`}>
                  <TableCell className="text-sm text-slate-600">{r.student.nisn}</TableCell>
                  <TableCell className="text-sm font-medium text-slate-800">{r.student.name}</TableCell>
                  <TableCell className="text-sm text-slate-600">{r.student.class_name}</TableCell>
                  <TableCell>
                    {r.status === "lunas" && (
                      <Badge className="rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-50" data-testid={`spp-status-lunas-${r.student.nisn}`}>Lunas</Badge>
                    )}
                    {r.status === "sebagian" && (
                      <Badge className="rounded-full bg-amber-50 text-amber-600 hover:bg-amber-50" data-testid={`spp-status-sebagian-${r.student.nisn}`}>Sebagian {r.percent}%</Badge>
                    )}
                    {r.status === "belum" && (
                      <Badge className="rounded-full bg-rose-50 text-rose-600 hover:bg-rose-50" data-testid={`spp-status-belum-${r.student.nisn}`}>Belum</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-sm font-medium text-slate-900">
                    {r.total_paid > 0 ? (
                      <span>
                        {formatRupiah(r.total_paid)}
                        {r.status === "sebagian" && (
                          <span className="block text-xs font-normal text-slate-400">dari {formatRupiah(data.spp_nominal)}</span>
                        )}
                      </span>
                    ) : "-"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {canEdit && r.status !== "lunas" && (
                        <Button size="sm" onClick={() => openPay(r)} data-testid={`spp-pay-button-${r.student.nisn}`} className="gap-1.5 rounded-md bg-[#0060AF] hover:bg-[#004B87] active:scale-[0.98]">
                          <Wallet className="h-3.5 w-3.5" strokeWidth={1.5} /> {r.status === "sebagian" ? "Cicilan" : "Bayar"}
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => openHistory(r)} data-testid={`spp-history-button-${r.student.nisn}`} className="gap-1.5 text-slate-600">
                        <History className="h-3.5 w-3.5" strokeWidth={1.5} /> Riwayat
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={!!payTarget} onOpenChange={() => setPayTarget(null)}>
        <DialogContent aria-describedby={undefined} data-testid="spp-pay-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Catat Pembayaran SPP</DialogTitle>
          </DialogHeader>
          {payTarget && (
            <form onSubmit={handlePay} className="space-y-4">
              <div className="rounded-lg bg-slate-50 px-4 py-3">
                <p className="text-sm font-medium text-slate-800">{payTarget.name}</p>
                <p className="text-xs text-slate-500">{payTarget.class_name} · NISN {payTarget.nisn} · {BULAN[Number(month) - 1]} {year}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount">Jumlah (Rp)</Label>
                <Input id="amount" type="number" min="0" data-testid="spp-amount-input" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Metode Pembayaran</Label>
                <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                  <SelectTrigger data-testid="spp-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Tunai" data-testid="spp-method-tunai">Tunai</SelectItem>
                    <SelectItem value="Transfer" data-testid="spp-method-transfer">Transfer Bank</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="note">Catatan (opsional)</Label>
                <Input id="note" data-testid="spp-note-input" value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} placeholder="Contoh: dibayar oleh ibu" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setPayTarget(null)} data-testid="spp-pay-cancel">Batal</Button>
                <Button type="submit" disabled={saving} data-testid="spp-pay-submit" className="bg-[#0060AF] hover:bg-[#004B87]">
                  {saving ? "Menyimpan..." : "Simpan Pembayaran"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyTarget} onOpenChange={() => setHistoryTarget(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-2xl" data-testid="spp-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Riwayat Pembayaran — {historyTarget?.name}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto">
            <Table data-testid="spp-history-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Periode</TableHead>
                  <TableHead>Tanggal Bayar</TableHead>
                  <TableHead>Metode</TableHead>
                  <TableHead className="text-right">Jumlah</TableHead>
                  {canEdit && <TableHead className="text-right">Aksi</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-slate-500">Belum ada riwayat pembayaran.</TableCell></TableRow>
                )}
                {history.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="text-sm text-slate-700">{BULAN[p.month - 1]} {p.year}</TableCell>
                    <TableCell className="text-sm text-slate-600">{formatTanggal(p.paid_at)}</TableCell>
                    <TableCell className="text-sm text-slate-600">{p.method}</TableCell>
                    <TableCell className="text-right text-sm font-medium text-slate-900">{formatRupiah(p.amount)}</TableCell>
                    {canEdit && (
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => setCancelTarget(p)} data-testid={`spp-cancel-${p.id}`} className="gap-1.5 text-rose-600 hover:text-rose-700">
                          <Undo2 className="h-3.5 w-3.5" strokeWidth={1.5} /> Batalkan
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={nominalOpen} onOpenChange={setNominalOpen}>
        <DialogContent aria-describedby={undefined} data-testid="spp-nominal-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Ubah Nominal SPP</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveNominal} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nominal">Nominal SPP per Bulan (Rp)</Label>
              <Input id="nominal" type="number" min="0" data-testid="spp-nominal-input" value={nominalVal} onChange={(e) => setNominalVal(e.target.value)} required />
              <p className="text-xs text-slate-400">Berlaku untuk perhitungan status lunas/sebagian selanjutnya.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setNominalOpen(false)} data-testid="spp-nominal-cancel">Batal</Button>
              <Button type="submit" disabled={savingNominal} data-testid="spp-nominal-submit" className="bg-[#0060AF] hover:bg-[#004B87]">
                {savingNominal ? "Menyimpan..." : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!cancelTarget}
        onOpenChange={() => setCancelTarget(null)}
        title="Batalkan Pembayaran"
        description={`Yakin ingin membatalkan pembayaran SPP ${cancelTarget ? `${BULAN[cancelTarget.month - 1]} ${cancelTarget.year}` : ""} senilai ${formatRupiah(cancelTarget?.amount)}?`}
        onConfirm={handleCancel}
        testId="confirm-cancel-payment"
      />
    </div>
  );
}
